/*
 * Section admin : sécurité et conformité.
 * 1. Compteurs du défi anti bot adaptatif sur les 7 derniers jours :
 *    défis déclenchés (comportements anormaux détectés) et défis réussis,
 *    avec un historique jour par jour pour repérer les tendances.
 *    Décomptes agrégés uniquement, aucune donnée personnelle.
 * 2. Alerte email automatique en cas de pic anormal sur 24 heures
 *    (seuil configuré côté serveur), avec bouton de test.
 * 3. Exports PDF : AIPD signée et guide propriétaire (actions restantes
 *    hors code).
 */

import { useCallback, useEffect, useState } from "react";
import { BellRing, FileDown, ListChecks, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  adminGetSecurity, adminDownloadAipdPdf, adminDownloadGuidePdf, adminTestSecurityAlert,
} from "@/lib/api";

const sectionClass =
  "rounded-2xl bg-white border border-[rgba(35,82,119,0.14)] p-6 shadow-[var(--shadow-sm)] space-y-5";

const tileClass =
  "rounded-xl border border-[rgba(35,82,119,0.14)] bg-[rgba(226,246,255,0.45)] p-4";

const outlineButtonClass =
  "w-full min-h-[44px] rounded-xl border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] font-heading font-semibold transition-colors duration-200";

/* Jour court en français depuis une date ISO (UTC). */
const dayLabel = (isoDate) => {
  try {
    return new Date(`${isoDate}T00:00:00Z`)
      .toLocaleDateString("fr-FR", { weekday: "short", timeZone: "UTC" })
      .replace(".", "");
  } catch (e) {
    return isoDate.slice(-2);
  }
};

/* Déclenche le téléchargement d'un Blob PDF dans le navigateur. */
const saveBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

/* ----- Graphique jour par jour (barres pures CSS, aucune dépendance) ----- */

const DailyChart = ({ daily }) => {
  const max = Math.max(1, ...daily.map((d) => d.triggered));
  const allZero = daily.every((d) => d.triggered === 0 && d.solved === 0);
  return (
    <div data-testid="admin-security-daily-chart">
      <p className="text-[11px] font-medium uppercase tracking-wide text-[#9E9E9E]">
        Tendance jour par jour
      </p>
      {allZero ? (
        <p className="mt-2 text-xs text-[#9E9E9E]" data-testid="admin-security-daily-empty">
          Aucun défi déclenché sur les 7 derniers jours. Tout est calme.
        </p>
      ) : (
        <>
          <div className="mt-2 flex items-end gap-2 h-[88px]">
            {daily.map((d) => {
              const triggeredH = Math.max(d.triggered > 0 ? 6 : 2, Math.round((d.triggered / max) * 80));
              const solvedH = Math.max(d.solved > 0 ? 6 : 0, Math.round((d.solved / max) * 80));
              return (
                <div
                  key={d.date}
                  className="flex-1 flex flex-col items-center justify-end gap-0.5"
                  data-testid={`admin-security-day-${d.date}`}
                  title={`${d.date} : ${d.triggered} déclenchés, ${d.solved} réussis`}
                >
                  <div className="w-full flex items-end justify-center gap-[3px]" style={{ height: 80 }}>
                    <div
                      className="w-[10px] rounded-t-sm bg-[rgba(35,82,119,0.25)]"
                      style={{ height: triggeredH }}
                      aria-hidden="true"
                    />
                    <div
                      className="w-[10px] rounded-t-sm bg-[#235277]"
                      style={{ height: solvedH }}
                      aria-hidden="true"
                    />
                  </div>
                  <span className="text-[10px] text-[#9E9E9E]">{dayLabel(d.date)}</span>
                </div>
              );
            })}
          </div>
          <div className="mt-2 flex items-center gap-4 text-[10px] text-[#9E9E9E]">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[rgba(35,82,119,0.25)]" aria-hidden="true" />
              Déclenchés
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#235277]" aria-hidden="true" />
              Réussis
            </span>
          </div>
        </>
      )}
    </div>
  );
};

/* ----- Tuiles des compteurs 7 jours ----- */

const StatTiles = ({ stats, solvedRate }) => (
  <div className="grid grid-cols-2 gap-3">
    <div className={tileClass} data-testid="admin-security-triggered-tile">
      <p className="text-[11px] font-medium uppercase tracking-wide text-[#9E9E9E]">
        Défis déclenchés
      </p>
      <p
        className="mt-1 font-heading text-2xl font-semibold text-[#235277]"
        data-testid="admin-security-triggered-count"
      >
        {stats.challenges_triggered}
      </p>
      <p className="text-[11px] text-[#9E9E9E]">comportements anormaux détectés</p>
    </div>
    <div className={tileClass} data-testid="admin-security-solved-tile">
      <p className="text-[11px] font-medium uppercase tracking-wide text-[#9E9E9E]">
        Défis réussis
      </p>
      <p
        className="mt-1 font-heading text-2xl font-semibold text-[#235277]"
        data-testid="admin-security-solved-count"
      >
        {stats.challenges_solved}
      </p>
      <p className="text-[11px] text-[#9E9E9E]">
        {solvedRate === null
          ? "personnes réelles débloquées"
          : `soit ${solvedRate}% des déclenchements`}
      </p>
    </div>
  </div>
);

/* ----- Encart de l'alerte email automatique ----- */

const AlertInfoBox = ({ threshold, alertTesting, onSendTest }) => (
  <div className="rounded-xl border border-[rgba(35,82,119,0.14)] bg-[rgba(226,246,255,0.3)] p-4 flex flex-col sm:flex-row sm:items-center gap-3">
    <div className="flex-1">
      <div className="flex items-center gap-2 text-[#235277]">
        <BellRing className="w-3.5 h-3.5" aria-hidden="true" />
        <p className="text-xs font-semibold text-[#060606]">Alerte email automatique</p>
      </div>
      <p className="mt-1 text-[11px] leading-relaxed text-[#9E9E9E]" data-testid="admin-security-alert-info">
        Si plus de {threshold} défis se déclenchent en 24 heures,
        une alerte part vers le destinataire du rapport hebdomadaire
        (au maximum une fois par jour).
      </p>
    </div>
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={onSendTest}
      disabled={alertTesting}
      data-testid="admin-security-alert-test-button"
      className="min-h-[36px] rounded-lg border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-white text-xs font-medium transition-colors duration-200"
    >
      {alertTesting ? "Envoi en cours" : "Envoyer un test"}
    </Button>
  </div>
);

/* ----- Panneau des documents de conformite (AIPD, guide) ----- */

const DocumentsPanel = ({ downloading, onDownload }) => (
  <div className="pt-1 border-t border-[rgba(35,82,119,0.1)]">
    <p className="pt-3 text-xs leading-relaxed text-[#333333]">
      Documents à portée de main : l'AIPD signée le 2 aout 2026 par Gino Fidelin,
      et le guide propriétaire qui rassemble toutes les actions restantes de ton
      côté (DNS Brevo, redirection après réservation, 2FA, DPA) en étapes simples.
    </p>
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Button
        type="button"
        variant="outline"
        onClick={() => onDownload("aipd")}
        disabled={downloading === "aipd"}
        data-testid="admin-aipd-download-button"
        className={outlineButtonClass}
      >
        <FileDown className="mr-2 w-4 h-4" aria-hidden="true" />
        {downloading === "aipd" ? "Préparation" : "AIPD signée (PDF)"}
      </Button>
      <Button
        type="button"
        variant="outline"
        onClick={() => onDownload("guide")}
        disabled={downloading === "guide"}
        data-testid="admin-guide-download-button"
        className={outlineButtonClass}
      >
        <ListChecks className="mr-2 w-4 h-4" aria-hidden="true" />
        {downloading === "guide" ? "Préparation" : "Guide propriétaire (PDF)"}
      </Button>
    </div>
  </div>
);

/* ----- Section principale ----- */

export const SecuritySection = ({ adminKey }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState("");
  const [alertTesting, setAlertTesting] = useState(false);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminGetSecurity(adminKey);
      setStats(data);
    } catch (e) {
      toast.error("Chargement impossible", {
        description: "Les compteurs de sécurité n'ont pas pu être récupérés.",
      });
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const download = async (kind) => {
    setDownloading(kind);
    try {
      if (kind === "aipd") {
        saveBlob(await adminDownloadAipdPdf(adminKey), "AIPD_CYCLIQ.pdf");
        toast.success("AIPD téléchargée", { description: "Le document signé est prêt à être archivé." });
      } else {
        saveBlob(await adminDownloadGuidePdf(adminKey), "GUIDE_PROPRIETAIRE_CYCLIQ.pdf");
        toast.success("Guide téléchargé", { description: "Toutes tes actions restantes, étape par étape." });
      }
    } catch (e) {
      toast.error("Téléchargement impossible", { description: "L'export PDF a échoué. Réessaie." });
    } finally {
      setDownloading("");
    }
  };

  const sendAlertTest = async () => {
    setAlertTesting(true);
    try {
      const result = await adminTestSecurityAlert(adminKey);
      if (result.status === "sent") {
        toast.success("Alerte de test envoyée", {
          description: "Vérifie la boite du destinataire du rapport hebdomadaire.",
        });
      } else if (result.reason === "no_recipient") {
        toast.error("Destinataire manquant", {
          description: "Renseigne le destinataire du rapport hebdomadaire puis enregistre.",
        });
      } else {
        toast.error("Envoi impossible", { description: "L'alerte de test n'est pas partie. Réessaie." });
      }
    } catch (e) {
      toast.error("Erreur", { description: "L'envoi du test a échoué. Réessaie." });
    } finally {
      setAlertTesting(false);
    }
  };

  const solvedRate =
    stats && stats.challenges_triggered > 0
      ? Math.round((stats.challenges_solved / stats.challenges_triggered) * 100)
      : null;

  return (
    <section className={sectionClass} data-testid="admin-security-section">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[#235277]">
            <ShieldCheck className="w-4 h-4" aria-hidden="true" />
            <h2 className="font-heading text-base font-semibold text-[#060606]">
              Sécurité et conformité
            </h2>
          </div>
          <p className="mt-1 text-xs leading-relaxed text-[#9E9E9E]">
            Le défi anti bot se déclenche uniquement en cas de comportement anormal.
            Compteurs agrégés des 7 derniers jours, aucune donnée personnelle.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={loadStats}
          disabled={loading}
          data-testid="admin-security-refresh-button"
          className="min-h-[36px] rounded-lg border-[rgba(35,82,119,0.3)] text-[#235277] hover:bg-[#E2F6FF] text-xs font-medium transition-colors duration-200"
        >
          <RefreshCw className={`mr-1.5 w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
          Actualiser
        </Button>
      </div>

      {loading && !stats ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-[84px] rounded-xl" />
            <Skeleton className="h-[84px] rounded-xl" />
          </div>
          <Skeleton className="h-[110px] rounded-xl" />
        </div>
      ) : stats ? (
        <>
          <StatTiles stats={stats} solvedRate={solvedRate} />
          <DailyChart daily={stats.daily || []} />
          <AlertInfoBox
            threshold={stats.alert_threshold_per_day}
            alertTesting={alertTesting}
            onSendTest={sendAlertTest}
          />
        </>
      ) : (
        <p className="text-xs text-[#9E9E9E]" data-testid="admin-security-empty">
          Compteurs indisponibles pour le moment.
        </p>
      )}

      <DocumentsPanel downloading={downloading} onDownload={download} />
    </section>
  );
};
